/**
 * The view screen's persisted state (§12.4) and its translation into §5.2 request params.
 *
 * `{mode, range, filters, sort, feedSort}` is stored server-side at `view:<t>:<id>`; this module
 * owns the defaults, the sanitizer that makes a stored blob safe to trust, and the one place where
 * a range preset becomes concrete `from`/`to` bounds. Nothing here derives a *displayed* number —
 * it is request-parameter construction, which the server then re-validates.
 *
 * Posts and Feed keep **separate** sorts (`sort` / `feedSort`): they are two readings of the same
 * data, and ordering one has never been a reason to reorder the other.
 *
 * It also owns the `columns:posts` layout sanitizer and the pin helper both Posts grids share —
 * pure functions the node suite can reach, unlike the hooks that call them.
 */

import {
  BUCKET_GRANULARITIES,
  CONTENT_TYPES,
  DEFAULT_POSTS_SORT,
  DEFAULT_VISIBLE_POSTS_COLUMNS,
  LENGTH_UNITS,
  MAX_PERF_CONDITIONS,
  METRIC_KEYS,
  PERF_KINDS,
  POSTS_COLUMN_IDS,
  POSTS_SORT_KEYS,
  POST_TYPES,
  RANGE_PRESETS,
  SCHEDULE_HEATMAP_MODES,
  SORT_DIRS,
  VIEW_MODES,
  getPostsColumn,
  type BucketGranularity,
  type ContentType,
  type LengthUnit,
  type MetricKey,
  type PerfConditionDTO,
  type PerfKind,
  type PostType,
  type PostsColumnId,
  type PostsSortKey,
  type RangePreset,
  type ScheduleHeatmapMode,
  type SortDir,
  type ViewFilterParams,
  type ViewFiltersState,
  type ViewMode,
  type ViewRangeState,
  type ViewUiState,
} from '@lw/shared';

export type ViewSort = { key: PostsSortKey; dir: SortDir };

export type ViewState = {
  mode?: ViewMode;
  range: ViewRangeState;
  filters: ViewFiltersState;
  /** The Posts table's ordering (grid headers, Sort control in posts mode, Export). */
  sort: ViewSort;
  /** The Feed tab's ordering — independent of `sort` (§12.3), same vocabulary. */
  feedSort: ViewSort;
  /** Overview chart granularity override; absent = the server's auto-pick (§8.5). */
  granularity?: BucketGranularity;
  /** Schedule calendar face; absent = `volume` (§8.6). Independent of `matrixMode`. */
  calendarMode?: ScheduleHeatmapMode;
  /** Schedule day × hour matrix face; absent = `volume` (§8.6). */
  matrixMode?: ScheduleHeatmapMode;
};

export const DEFAULT_VIEW_STATE: ViewState = {
  range: { preset: '30d' },
  filters: {},
  // Two fresh literals, never one shared object: the two sorts diverge the moment either is set,
  // and an alias would make a change to one mutate the other's default identity.
  sort: { key: DEFAULT_POSTS_SORT.key, dir: DEFAULT_POSTS_SORT.dir },
  feedSort: { key: DEFAULT_POSTS_SORT.key, dir: DEFAULT_POSTS_SORT.dir },
};

export const RANGE_PRESET_LABEL: Record<RangePreset, string> = {
  '30d': 'Last 30 days',
  '60d': 'Last 60 days',
  '90d': 'Last 90 days',
  '6mo': 'Last 6 months',
  '12mo': 'Last 12 months',
  all: 'All time',
  custom: 'Custom…',
};

/**
 * A sort field named in full — for the Sort trigger and the Sort panel's `<select>`, which must
 * agree word for word.
 *
 * `exportLabel` is exactly the escape hatch for this: it exists because the grid header labels are
 * abbreviations that are too terse to stand alone (`R ×`, `Sv ×`, `Total eng.`), and the CSV writer
 * already resolves the same `exportLabel ?? label`.
 */
export function sortKeyLabel(key: PostsSortKey): string {
  const column = getPostsColumn(key);
  return column.exportLabel ?? column.label;
}

/* --------------------------------------------------------------- sanitize */

function pickEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' ? allowed.find((candidate) => candidate === value) : undefined;
}

function pickEnumList<T extends string>(value: unknown, allowed: readonly T[]): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const list = value
    .map((entry) => pickEnum(entry, allowed))
    .filter((entry): entry is T => entry !== undefined);
  return list.length > 0 ? [...new Set(list)] : undefined;
}

/** Exported for the global feed's own sanitizer, which stores two more id lists (FEED_SPEC). */
export function pickIdList(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const list = value.filter(
    (entry): entry is number => typeof entry === 'number' && Number.isInteger(entry) && entry > 0,
  );
  return list.length > 0 ? [...new Set(list)] : undefined;
}

function pickNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * The performance conditions of a stored blob, plus the pre-`perf` `perfMetric`+`perfMin` pair
 * migrated into one multiplier condition — a blob written before this shape keeps its filter.
 *
 * Deduped by (metric, kind), because two rows for the same pair would be two joins agreeing on the
 * stricter one, and capped at the API's own ceiling. First occurrence wins, so the migrated legacy
 * pair loses to an explicit condition on the same metric.
 */
function pickPerfConditions(
  raw: unknown,
  legacyMetric: unknown,
  legacyMin: unknown,
): PerfConditionDTO[] | undefined {
  const conditions: PerfConditionDTO[] = [];
  const seen = new Set<string>();

  const add = (metric: MetricKey, kind: PerfKind, min: number): void => {
    const key = `${metric}:${kind}`;
    if (seen.has(key) || conditions.length >= MAX_PERF_CONDITIONS) return;
    seen.add(key);
    conditions.push({ metric, kind, min: Math.max(0, min) });
  };

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (typeof entry !== 'object' || entry === null) continue;
      const source = entry as Record<string, unknown>;
      const metric = pickEnum<MetricKey>(source.metric, METRIC_KEYS);
      const kind = pickEnum<PerfKind>(source.kind, PERF_KINDS);
      const min = pickNumber(source.min);
      if (metric !== undefined && kind !== undefined && min !== undefined) add(metric, kind, min);
    }
  }

  const migratedMetric = pickEnum<MetricKey>(legacyMetric, METRIC_KEYS);
  const migratedMin = pickNumber(legacyMin);
  if (migratedMetric !== undefined && migratedMin !== undefined) {
    add(migratedMetric, 'multiplier', migratedMin);
  }

  return conditions.length > 0 ? conditions : undefined;
}

/**
 * The shared §5.2 filter vocabulary, made safe to trust. Exported because the global feed persists
 * the same vocabulary under a different key: one sanitizer, so the two can never disagree about
 * what a stored `perf` or `lenUnit` means.
 */
export function sanitizeViewFilters(raw: unknown): ViewFiltersState {
  if (typeof raw !== 'object' || raw === null) return {};
  const source = raw as Record<string, unknown>;
  const filters: ViewFiltersState = {};

  const postTypes = pickEnumList<PostType>(source.postTypes, POST_TYPES);
  if (postTypes) filters.postTypes = postTypes;

  const contentTypes = pickEnumList<ContentType>(source.contentTypes, CONTENT_TYPES);
  if (contentTypes) filters.contentTypes = contentTypes;

  const authorIds = pickIdList(source.authorIds);
  if (authorIds) filters.authorIds = authorIds;

  const perf = pickPerfConditions(source.perf, source.perfMetric, source.perfMin);
  if (perf) filters.perf = perf;

  // The unit survives on its own: the picker remembers Words/Characters with both bounds empty,
  // and a unit without a bound is not a filter — nothing downstream sends or counts it.
  const lenUnit = pickEnum<LengthUnit>(source.lenUnit, LENGTH_UNITS);
  const lenMin = pickNumber(source.lenMin);
  const lenMax = pickNumber(source.lenMax);
  if (lenUnit !== undefined) {
    filters.lenUnit = lenUnit;
    if (lenMin !== undefined) filters.lenMin = Math.max(0, Math.trunc(lenMin));
    if (lenMax !== undefined) filters.lenMax = Math.max(0, Math.trunc(lenMax));
  }

  if (typeof source.hasMedia === 'boolean') filters.hasMedia = source.hasMedia;
  if (typeof source.edited === 'boolean') filters.edited = source.edited;
  if (typeof source.hasLink === 'boolean') filters.hasLink = source.hasLink;
  if (typeof source.saved === 'boolean') filters.saved = source.saved;
  if (typeof source.q === 'string' && source.q.trim().length > 0) filters.q = source.q.slice(0, 200);

  return filters;
}

export function sanitizeRange(raw: unknown): ViewRangeState {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_VIEW_STATE.range };
  const source = raw as Record<string, unknown>;
  const preset = pickEnum<RangePreset>(source.preset, RANGE_PRESETS);
  if (preset === undefined) return { ...DEFAULT_VIEW_STATE.range };
  if (preset !== 'custom') return { preset };
  const from = pickNumber(source.from);
  const to = pickNumber(source.to);
  if (from === undefined || to === undefined || to < from) return { ...DEFAULT_VIEW_STATE.range };
  return { preset, from, to };
}

export function sanitizeSort(raw: unknown): ViewSort {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_VIEW_STATE.sort };
  const source = raw as Record<string, unknown>;
  const key = pickEnum<PostsSortKey>(source.key, POSTS_SORT_KEYS);
  const dir = pickEnum<SortDir>(source.dir, SORT_DIRS);
  if (key === undefined || dir === undefined) return { ...DEFAULT_VIEW_STATE.sort };
  return { key, dir };
}

/** Stored UI state is user-writable JSON: never trust its shape, always fall back to defaults. */
export function sanitizeViewState(raw: unknown): ViewState {
  if (typeof raw !== 'object' || raw === null) {
    return { ...DEFAULT_VIEW_STATE, filters: {}, range: { ...DEFAULT_VIEW_STATE.range } };
  }
  const source = raw as Record<string, unknown>;
  const mode = pickEnum<ViewMode>(source.mode, VIEW_MODES);
  const granularity = pickEnum<BucketGranularity>(source.granularity, BUCKET_GRANULARITIES);
  const calendarMode = pickEnum<ScheduleHeatmapMode>(source.calendarMode, SCHEDULE_HEATMAP_MODES);
  const matrixMode = pickEnum<ScheduleHeatmapMode>(source.matrixMode, SCHEDULE_HEATMAP_MODES);
  const state: ViewState = {
    range: sanitizeRange(source.range),
    filters: sanitizeViewFilters(source.filters),
    sort: sanitizeSort(source.sort),
    // Blobs written before Posts and Feed sorted separately carry one `sort`: seed the feed from
    // it, so a customized ordering survives the split once and the two diverge from there.
    feedSort: sanitizeSort(source.feedSort ?? source.sort),
  };
  if (mode !== undefined) state.mode = mode;
  if (granularity !== undefined) state.granularity = granularity;
  if (calendarMode !== undefined) state.calendarMode = calendarMode;
  if (matrixMode !== undefined) state.matrixMode = matrixMode;
  return state;
}

/**
 * What actually goes to `PUT /api/ui-state/:key` (the schema of `ViewUiStateSchema`).
 *
 * A strict whitelist: a field missing here is silently dropped on every write, however well the
 * in-memory state tracks it.
 */
export function toStoredViewState(state: ViewState): ViewUiState {
  const stored: ViewUiState = {
    range: state.range,
    filters: state.filters,
    sort: state.sort,
    feedSort: state.feedSort,
  };
  if (state.mode !== undefined) stored.mode = state.mode;
  if (state.granularity !== undefined) stored.granularity = state.granularity;
  if (state.calendarMode !== undefined) stored.calendarMode = state.calendarMode;
  if (state.matrixMode !== undefined) stored.matrixMode = state.matrixMode;
  return stored;
}

/* ------------------------------------------------------------------ range */

const DAY_MS = 86_400_000;

/** Anchors "now" to the next whole minute so query keys stay stable between renders. */
export function anchorNow(now = Date.now()): number {
  return Math.ceil(now / 60_000) * 60_000;
}

function minusMonths(to: number, months: number): number {
  const date = new Date(to);
  date.setMonth(date.getMonth() - months);
  return date.getTime();
}

export type ResolvedRange = { from: number; to: number };

/**
 * Preset → concrete bounds. `all` sends `from = 0` rather than omitting the params, because an
 * omitted range means "last 30 days" server-side (§5.2).
 */
export function resolveRange(range: ViewRangeState, now: number): ResolvedRange {
  const to = now;
  switch (range.preset) {
    case '30d':
      return { from: to - 30 * DAY_MS, to };
    case '60d':
      return { from: to - 60 * DAY_MS, to };
    case '90d':
      return { from: to - 90 * DAY_MS, to };
    case '6mo':
      return { from: minusMonths(to, 6), to };
    case '12mo':
      return { from: minusMonths(to, 12), to };
    case 'all':
      return { from: 0, to };
    case 'custom':
      return range.from !== undefined && range.to !== undefined
        ? { from: range.from, to: range.to }
        : { from: to - 30 * DAY_MS, to };
  }
}

/* ----------------------------------------------------------------- params */

export type ViewParamOptions = {
  /** Author filtering only exists in group views (§5.2). */
  isGroup: boolean;
  /** `q` is the Posts-mode search (§5.2) — Metrics and Feed do not receive it. */
  withQuery?: boolean;
};

/**
 * Takes the filters rather than the whole state: the global feed persists the same vocabulary in a
 * differently-shaped blob, and this is the one place that turns any of it into §5.2 params.
 */
export function toFilterParams(
  filters: ViewFiltersState,
  resolved: ResolvedRange,
  options: ViewParamOptions,
): Partial<ViewFilterParams> {
  const params: Partial<ViewFilterParams> = { from: resolved.from, to: resolved.to };

  if (filters.postTypes?.length) params.postTypes = filters.postTypes;
  if (filters.contentTypes?.length) params.contentTypes = filters.contentTypes;
  if (options.isGroup && filters.authorIds?.length) params.authorIds = filters.authorIds;
  if (filters.perf?.length) params.perf = filters.perf.map((condition) => ({ ...condition }));
  if (filters.lenUnit !== undefined && (filters.lenMin !== undefined || filters.lenMax !== undefined)) {
    params.lenUnit = filters.lenUnit;
    if (filters.lenMin !== undefined) params.lenMin = filters.lenMin;
    if (filters.lenMax !== undefined) params.lenMax = filters.lenMax;
  }
  if (filters.hasMedia !== undefined) params.hasMedia = filters.hasMedia;
  if (filters.edited !== undefined) params.edited = filters.edited;
  if (filters.hasLink !== undefined) params.hasLink = filters.hasLink;
  if (filters.saved !== undefined) params.saved = filters.saved;
  if (options.withQuery === true && filters.q !== undefined && filters.q.trim().length > 0) {
    params.q = filters.q.trim();
  }
  return params;
}

/**
 * Adds or removes one author from the `authorIds` filter — the Overview per-member table's row
 * click, which is the FilterPanel's author checkbox by another name (same multi-select, same
 * persisted key). The key is deleted when the last id leaves, because "no authors selected" is not
 * a filter (`toFilterParams` and `activeFilterCount` both read the list's length).
 */
export function toggleAuthorId(filters: ViewFiltersState, id: number): ViewFiltersState {
  const current = filters.authorIds ?? [];
  const next = current.includes(id)
    ? current.filter((entry) => entry !== id)
    : [...current, id];
  const { authorIds: _dropped, ...rest } = filters;
  return next.length > 0 ? { ...rest, authorIds: next } : rest;
}

/** Badge on the Filter button (PRD "Filter control"). */
export function activeFilterCount(filters: ViewFiltersState, isGroup: boolean): number {
  let count = 0;
  if (filters.postTypes?.length) count += 1;
  if (filters.contentTypes?.length) count += 1;
  if (isGroup && filters.authorIds?.length) count += 1;
  // One badge unit per condition: the panel/sidebar can now hold several at once.
  count += filters.perf?.length ?? 0;
  if (filters.lenUnit !== undefined && (filters.lenMin !== undefined || filters.lenMax !== undefined)) {
    count += 1;
  }
  if (filters.hasMedia !== undefined) count += 1;
  if (filters.edited !== undefined) count += 1;
  if (filters.hasLink !== undefined) count += 1;
  if (filters.saved !== undefined) count += 1;
  return count;
}

/**
 * Does the empty state read "no matches" or "nothing here"? `withQuery` must mirror what the mode
 * actually sent: only Posts sends `q` (§5.2), so a leftover search term must not make the feed
 * blame a filter it never applied.
 */
export function hasAnyFilter(
  filters: ViewFiltersState,
  isGroup: boolean,
  withQuery: boolean,
): boolean {
  return (
    activeFilterCount(filters, isGroup) > 0 ||
    (withQuery && (filters.q ?? '').trim().length > 0)
  );
}

/* -------------------------------------------------- stored column layout */

/**
 * Moves `pinned` to index 0, leaving every other id's relative order alone. `order` comes back
 * untouched when nothing is pinned or the stored blob never named the pinned id.
 *
 * The pin is enforced on every *read and write* of a layout, not seeded once: a custom order that
 * dragged the post column elsewhere is normalised instead of re-seeded, so the rest of it survives.
 */
export function pinColumnFirst<Id extends string>(order: readonly Id[], pinned: Id | null): Id[] {
  if (pinned === null) return [...order];
  const at = order.indexOf(pinned);
  if (at <= 0) return [...order];
  return [pinned, ...order.filter((id) => id !== pinned)];
}

/** The Posts grid's frozen column (§12.3) — first seat, sticky left, still hideable. */
export const PINNED_POSTS_COLUMN: PostsColumnId = 'preview';

export type ColumnLayout = {
  /** Every known column, in display order — the pinned one first. */
  order: PostsColumnId[];
  hidden: PostsColumnId[];
};

const KNOWN_POSTS_COLUMN_IDS = new Set<string>(POSTS_COLUMN_IDS);
const DEFAULT_HIDDEN_POSTS_COLUMNS: PostsColumnId[] = POSTS_COLUMN_IDS.filter(
  (id) => !DEFAULT_VISIBLE_POSTS_COLUMNS.includes(id),
);

export const DEFAULT_COLUMN_LAYOUT: ColumnLayout = {
  // Built through the pin, so `reset` and the read-failure seed store exactly what is displayed.
  order: pinColumnFirst(POSTS_COLUMN_IDS, PINNED_POSTS_COLUMN),
  hidden: [...DEFAULT_HIDDEN_POSTS_COLUMNS],
};

function sanitizePostsColumnIds(value: unknown): PostsColumnId[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const ids: PostsColumnId[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string' || !KNOWN_POSTS_COLUMN_IDS.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    ids.push(entry as PostsColumnId);
  }
  return ids;
}

/**
 * Stored order first, then any column the stored value never heard of in canonical order — and the
 * pinned column pulled to the front whatever seat the blob gave it.
 */
export function sanitizeColumnLayout(raw: unknown): ColumnLayout {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_COLUMN_LAYOUT };
  const source = raw as Record<string, unknown>;
  const storedOrder = sanitizePostsColumnIds(source.order);
  const order =
    storedOrder.length === 0
      ? [...POSTS_COLUMN_IDS]
      : [...storedOrder, ...POSTS_COLUMN_IDS.filter((id) => !storedOrder.includes(id))];
  const hidden =
    source.hidden === undefined
      ? [...DEFAULT_HIDDEN_POSTS_COLUMNS]
      : sanitizePostsColumnIds(source.hidden);
  return { order: pinColumnFirst(order, PINNED_POSTS_COLUMN), hidden };
}
